import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS, getAuthHeaders } from '../../config/api';

function formatEUR(amount) {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  } catch {
    return `${amount.toFixed(0)} €`;
  }
}

/**
 * Hook pour récupérer le salaire depuis la table payroll_slips
 */
function usePayrollSalary(operatorId, currentMonth) {
  const [loading, setLoading] = useState(true);
  const [salary, setSalary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!operatorId || !currentMonth) {
      setLoading(false);
      setSalary(null);
      return;
    }

    const fetchPayroll = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Formater le mois en YYYY-MM
        const year = currentMonth.getFullYear();
        const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
        const periodMonth = `${year}-${month}`;
        
        // Appeler l'API payroll avec les filtres
        const url = `${API_ENDPOINTS.PAYROLL_LIST()}?operator_id=${operatorId}&period_month=${periodMonth}`;
        console.log(`🔍 [SalaryCell] Fetching payroll from URL:`, url);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include'
        });
        
        console.log(`🔍 [SalaryCell] Response status:`, response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }

        const data = await response.json();
        
        console.log(`🔍 [SalaryCell] API Response for operator ${operatorId}, month ${periodMonth}:`, data);
        console.log(`🔍 [SalaryCell] Items count:`, data.items ? data.items.length : 0);
        console.log(`🔍 [SalaryCell] Items:`, data.items);
        
        // Vérifier si on a des résultats
        if (data.items && data.items.length > 0) {
          // Prendre le premier résultat (il devrait n'y en avoir qu'un par opérateur/mois)
          const payrollSlip = data.items[0];
          console.log(`✅ [SalaryCell] Found payroll slip:`, payrollSlip);
          // Convertir salary_net en nombre (car le backend peut retourner une string)
          const salaryValue = parseFloat(payrollSlip.salary_net);
          console.log(`✅ [SalaryCell] Parsed salary value:`, salaryValue, typeof salaryValue);
          setSalary(salaryValue);
        } else {
          // Pas de données pour ce mois
          console.log(`❌ [SalaryCell] No payroll data found for operator ${operatorId}, month ${periodMonth}`);
          setSalary(null);
        }
      } catch (err) {
        console.error('Erreur lors du chargement du salaire:', err);
        setError(err);
        setSalary(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPayroll();
  }, [operatorId, currentMonth]);

  return { loading, salary, error };
}

export function SalaryCell({ operatorId, currentMonth }) {
  const { loading, salary, error } = usePayrollSalary(operatorId, currentMonth);

  console.log(`💵 SalaryCell - operatorId: ${operatorId}, month: ${currentMonth?.toISOString()}, loading: ${loading}, error: ${error}, salary: ${salary}`);

  if (loading) {
    return <span className="salary-value salary-loading">…</span>;
  }

  if (error) {
    return <span className="salary-value">-</span>;
  }

  // Si pas de données de salaire pour ce mois
  if (salary === null || salary === undefined) {
    return <span className="salary-value">-</span>;
  }

  if (!Number.isFinite(salary) || salary <= 0) {
    return <span className="salary-value">-</span>;
  }

  return <span className="salary-value salary-computed">{formatEUR(salary)}</span>;
}
